import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from "../../App"; // Import from App.js
import DashboardHeader from "../../components/app_components/DashboardHeader";
import DashboardSidebar from "../../components/app_components/DashboardSidebar";
import { 
  Home, Bell, BellDot, ShoppingBag, Settings, 
  Users, ShoppingCart, Mail, AlertCircle, 
  CheckCircle, CreditCard, Calendar, Share2, Loader2,
  Mail as MailIcon, Smartphone, Save, RefreshCw
} from 'lucide-react';
import { useTranslation } from '../../contexts/LanguageContext';

const Notifications = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout, validateToken, httpClient } = useAuth(); // Get auth context
  
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  const [activeTab, setActiveTab] = useState('all');
  const [notificationSettings, setNotificationSettings] = useState({
    global_settings: {
      ShareSubs: { email: true, inApp: true },
      Security: { email: true, inApp: true },
      Promotion: { email: true, inApp: true },
      FreeTrialAlert: { email: true, inApp: true },
      PriceRadarAlert: { email: true, inApp: true }
    },
    merchant_promo_settings: {}
  });
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingSettings, setLoadingSettings] = useState(true);
  const [savingSettings, setSavingSettings] = useState(false);
  const [error, setError] = useState(null);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [expandedId, setExpandedId] = useState(null);
  const [markingAllAsRead, setMarkingAllAsRead] = useState(false);
  const [animatingNotifications, setAnimatingNotifications] = useState(new Set());
  const [animationStage, setAnimationStage] = useState('idle');
  
  // Add premium status state
  const [isPremium, setIsPremium] = useState(false);

  const unreadCount = notifications.filter(n => !n.read).length;

  const navItems = [
    { icon: Home, label: t('bottomNav.home', 'Home'), path: "/dashboard" },
    { 
      icon: unreadCount > 0 ? BellDot : Bell,
      label: t('bottomNav.notifications', 'Notifications'),
      path: "/notifications",
      badge: unreadCount > 0 ? unreadCount : null
    },
    { icon: ShoppingCart, label: t('bottomNav.marketplace', 'Marketplace'), path: "/marketplace" },
    { icon: Share2, label: t('bottomNav.shareSubs', 'Share Subs'), path: "/share-subs" },
    { icon: ShoppingBag, label: t('bottomNav.connectBank', 'Connect Bank'), path: "/connectBank" },
    { icon: Users, label: t('bottomNav.partners', 'Partners'), path: "/partners" },
    { icon: Settings, label: t('bottomNav.settings', 'Settings'), path: "/account-settings" },
  ];

  // Fetch premium status
  const fetchPremiumStatus = async () => {
    try {
      if (!user) {
        setIsPremium(false);
        return;
      }

      // Try dedicated premium status endpoint
      try {
        const premiumRes = await httpClient.request('/api/user/premium-status');
        if (premiumRes.ok) {
          const premiumData = await premiumRes.json();
          setIsPremium(premiumData.isPremium);
          return;
        }
      } catch (premiumError) {
        console.error('Error fetching premium status:', premiumError);
      }

      // Fallback to user account endpoint
      const fallbackRes = await httpClient.request('/api/user/account');
      if (fallbackRes.ok) {
        const fallbackData = await fallbackRes.json();
        setIsPremium(fallbackData.userPlan === 'premium');
      }
    } catch (error) {
      console.error('Error fetching user premium status:', error);
      setIsPremium(false);
    }
  };

  useEffect(() => {
    if (user) {
      fetchNotifications();
      fetchNotificationSettings();
      fetchPremiumStatus(); // Fetch premium status
    } else {
      navigate("/demo");
    }
  }, [user, navigate]);

  // Mark all notifications as read when notifications are loaded
  useEffect(() => {
    if (!loading && notifications.length > 0 && unreadCount > 0) {
      smoothAnimateMarkAllAsRead();
    }
  }, [loading, notifications]);

  const fetchNotificationSettings = async () => {
    try {
      setLoadingSettings(true);
      
      if (!httpClient) {
        setError(t('notifications.userIdNotFound', 'User not authenticated. Please log in again.'));
        setLoadingSettings(false);
        return;
      }
      
      const response = await httpClient.request('/api/notification-settings');
      
      if (!response.ok) {
        throw new Error(t('notifications.failedFetchSettings', 'Failed to fetch notification settings'));
      }

      const data = await response.json();
      setNotificationSettings(data);
    } catch (err) {
      console.error('Notification settings fetch error:', err);
      setError(t('notifications.failedLoadSettings', 'Failed to load notification settings'));
    } finally {
      setLoadingSettings(false);
    }
  };

  const saveNotificationSettings = async () => {
    try {
      setSavingSettings(true);
      
      if (!httpClient) {
        setError(t('notifications.userIdNotFound', 'User not authenticated. Please log in again.'));
        setSavingSettings(false);
        return;
      }
      
      const response = await httpClient.request('/api/notification-settings', {
        method: 'PUT',
        body: JSON.stringify({
          global_settings: notificationSettings.global_settings,
          merchant_promo_settings: notificationSettings.merchant_promo_settings
        })
      });
      
      if (!response.ok) {
        throw new Error(t('notifications.failedUpdateSettings', 'Failed to update notification settings'));
      }

      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000);
    } catch (err) {
      console.error('Update settings error:', err);
      setError(t('notifications.failedUpdateSettings', 'Failed to update notification settings'));
    } finally {
      setSavingSettings(false);
    }
  };

  const toggleNotificationSetting = (category, channel) => {
    setNotificationSettings(prev => ({
      ...prev,
      global_settings: {
        ...prev.global_settings,
        [category]: {
          ...prev.global_settings[category],
          [channel]: !prev.global_settings[category][channel]
        }
      }
    }));
  };

  const fetchNotifications = async () => {
    try {
      setLoading(true);
      setError(null);
      
      if (!httpClient) {
        setError(t('notifications.userIdNotFound', 'User not authenticated. Please log in again.'));
        setLoading(false);
        return;
      }
      
      const response = await httpClient.request('/api/notifications');
      
      if (!response.ok) {
        throw new Error(t('notifications.failedFetchNotifications', 'Failed to fetch notifications'));
      }

      const data = await response.json();
      
      const mappedNotifications = data.map(notif => {
        const isoDate = notif.notification_time;
        
        return {
          id: notif._id,
          type: notif.type,
          title: notif.title,
          message: notif.msg,
          date: formatRelativeDate(isoDate),
          read: notif.read_status,
          icon: getIconByType(notif.type),
          fullDate: formatFullDate(isoDate)
        };
      });
      
      setNotifications(mappedNotifications);
      
    } catch (err) {
      console.error('Notification fetch error:', err);
      
      // Handle token expiration
      if (err.status === 401) {
        const revalidated = await validateToken();
        if (!revalidated) {
          navigate("/demo");
          return;
        }
        // Retry the request
        return fetchNotifications();
      }
      
      setError(t('notifications.failedLoadNotifications', 'Failed to load notifications. Please try again later.'));
    } finally {
      setLoading(false);
    }
  };

  const smoothAnimateMarkAllAsRead = async () => {
    try {
      setMarkingAllAsRead(true);
      setAnimationStage('fading');
      
      if (!httpClient) {
        setMarkingAllAsRead(false);
        setAnimationStage('idle');
        return;
      }
      
      // Get all unread notification IDs
      const unreadIds = notifications
        .filter(n => !n.read)
        .map(n => n.id);
      
      // Add all unread notifications to animation set
      setAnimatingNotifications(new Set(unreadIds));
      
      // Start the visual animation first with a small delay
      setTimeout(async () => {
        try {
          // Call the API endpoint to mark all notifications as read
          const response = await httpClient.request('/api/notifications/mark-all-read', {
            method: 'PUT'
          });
          
          if (!response.ok) {
            throw new Error(t('notifications.failedMarkAllRead', 'Failed to mark all notifications as read'));
          }
          
          // Update local state to mark all notifications as read
          setNotifications(prev => 
            prev.map(notif => ({ ...notif, read: true }))
          );
          
          // Wait for animation to complete before clearing
          setTimeout(() => {
            setAnimatingNotifications(new Set());
            setAnimationStage('done');
            
            // Reset animation stage after a moment
            setTimeout(() => {
              setAnimationStage('idle');
            }, 200);
          }, 1000);
            
          console.log('All notifications marked as read');
        } catch (error) {
          console.error('Mark all as read API error:', error);
          setAnimatingNotifications(new Set());
          setAnimationStage('idle');
        }
      }, 100);
      
    } catch (err) {
      console.error('Mark all as read error:', err);
      setAnimatingNotifications(new Set());
      setAnimationStage('idle');
    } finally {
      setTimeout(() => {
        setMarkingAllAsRead(false);
      }, 1200);
    }
  };

  const handleNotificationClick = async (id) => {
    try {
      const notification = notifications.find(n => n.id === id);
      
      if (notification && !notification.read) {
        // Add to animation set
        setAnimatingNotifications(prev => new Set([...prev, id]));
        
        setTimeout(async () => {
          try {
            const response = await httpClient.request(`/api/notifications/${id}/read`, {
              method: 'PUT'
            });
            
            if (!response.ok) {
              throw new Error(t('notifications.failedMarkRead', 'Failed to update status'));
            }
            
            // Update local state
            setNotifications(prev => 
              prev.map(notif => 
                notif.id === id ? { ...notif, read: true } : notif
              )
            );
            
            // Remove from animation set after animation completes
            setTimeout(() => {
              setAnimatingNotifications(prev => {
                const newSet = new Set(prev);
                newSet.delete(id);
                return newSet;
              });
            }, 800);
          } catch (error) {
            console.error('Mark as read API error:', error);
            setAnimatingNotifications(prev => {
              const newSet = new Set(prev);
              newSet.delete(id);
              return newSet;
            });
          }
        }, 20);
      }
      
      setExpandedId(expandedId === id ? null : id);
      
    } catch (err) {
      console.error('Mark as read error:', err);
      setError(t('notifications.failedMarkRead', 'Failed to mark as read'));
      setAnimatingNotifications(prev => {
        const newSet = new Set(prev);
        newSet.delete(id);
        return newSet;
      });
    }
  };

  const formatRelativeDate = (isoDate) => {
    try {
      if (!isoDate) return t('notifications.now', 'Now');
      
      const date = new Date(isoDate);
      if (isNaN(date.getTime())) return t('notifications.now', 'Now');
      
      const now = new Date();
      const diffMs = now - date;
      const diffSec = Math.floor(diffMs / 1000);
      const diffMin = Math.floor(diffSec / 60);
      
      if (diffMin < 5) return t('notifications.now', 'Now');
      if (diffMin < 60) return `${diffMin} ${t('notifications.minutesAgo', 'min ago')}`;
      
      const diffHr = Math.floor(diffMin / 60);
      if (diffHr < 24) return `${diffHr} ${t('notifications.hoursAgo', 'hr ago')}`;
      
      const diffDay = Math.floor(diffHr / 24);
      if (diffDay === 1) return t('notifications.yesterday', 'Yesterday');
      return `${diffDay} ${t('notifications.daysAgo', 'days ago')}`;
    } catch (e) {
      console.error('Date error:', e);
      return t('notifications.now', 'Now');
    }
  };

  const formatFullDate = (isoDate) => {
    try {
      if (!isoDate) return '';
      
      const date = new Date(isoDate);
      if (isNaN(date.getTime())) return '';
      
      return date.toLocaleString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (e) {
      console.error('Full date error:', e);
      return '';
    }
  };

  const getIconByType = (type) => {
    const icons = {
      payment: CreditCard,
      security: AlertCircle,
      updates: CheckCircle,
      promotions: Mail,
      sharesubs: Share2,
      freetrialalert: Calendar,
      priceradaralert: Bell,
      info: Bell
    };
    return icons[type?.toLowerCase()] || Bell;
  };

  const filteredNotifications = activeTab === 'all' 
    ? notifications 
    : notifications.filter(n => n.type === activeTab);

  const NotificationSettingToggle = ({ category, label, description }) => (
    <div className="bg-white p-4 rounded-lg border border-gray-200 mb-4">
      <div className="flex justify-between items-start mb-3">
        <div>
          <h3 className="font-medium text-gray-900">{label}</h3>
          <p className="text-sm text-gray-500 mt-1">{description}</p>
        </div>
      </div>
      <div className="flex justify-between items-center mt-4">
        <div className="flex items-center gap-2">
          <MailIcon className="h-4 w-4 text-gray-500" />
          <span className="text-sm">{t('notifications.email', 'Email')}</span>
        </div>
        <button
          onClick={() => toggleNotificationSetting(category, 'email')}
          disabled={savingSettings}
          className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-[#253985] focus:ring-offset-2 ${
            notificationSettings.global_settings[category].email 
              ? 'bg-[#253985]' 
              : 'bg-gray-200'
          }`}
        >
          <span
            className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
              notificationSettings.global_settings[category].email 
                ? 'translate-x-6' 
                : 'translate-x-1'
            }`}
          />
        </button>
      </div>
      <div className="flex justify-between items-center mt-3">
        <div className="flex items-center gap-2">
          <Smartphone className="h-4 w-4 text-gray-500" />
          <span className="text-sm">{t('notifications.inApp', 'In-App')}</span>
        </div>
        <button
          onClick={() => toggleNotificationSetting(category, 'inApp')}
          disabled={savingSettings}
          className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-[#253985] focus:ring-offset-2 ${
            notificationSettings.global_settings[category].inApp 
              ? 'bg-[#253985]' 
              : 'bg-gray-200'
          }`}
        >
          <span
            className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
              notificationSettings.global_settings[category].inApp 
                ? 'translate-x-6' 
                : 'translate-x-1'
            }`}
          />
        </button>
      </div>
    </div>
  );

  // Add CSS for smoother animations
  const animationStyles = `
    @keyframes smoothFadeOutBlue {
      0% {
        background-color: rgba(219, 234, 254, 1);
        border-color: rgba(37, 57, 133, 1);
        transform: translateY(0);
      }
      50% {
        background-color: rgba(219, 234, 254, 0.6);
        border-color: rgba(37, 57, 133, 0.6);
        transform: translateY(-1px);
      }
      80% {
        background-color: rgba(219, 234, 254, 0.2);
        border-color: rgba(37, 57, 133, 0.2);
        transform: translateY(0);
      }
      100% {
        background-color: rgba(255, 255, 255, 1);
        border-color: rgba(229, 231, 235, 1);
        transform: translateY(0);
      }
    }
    
    @keyframes smoothFadeOutBadge {
      0% {
        opacity: 1;
        transform: scale(1);
      }
      30% {
        opacity: 0.8;
        transform: scale(1.05);
      }
      70% {
        opacity: 0.4;
        transform: scale(0.95);
      }
      100% {
        opacity: 0;
        transform: scale(0.9);
      }
    }
    
    .smooth-fade-out-blue {
      animation: smoothFadeOutBlue 1s cubic-bezier(0.4, 0, 0.2, 1) forwards;
    }
    
    .smooth-fade-out-badge {
      animation: smoothFadeOutBadge 0.8s cubic-bezier(0.4, 0, 0.2, 1) forwards;
    }
    
    .notification-container {
      transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    }
    
    .staggered-animation {
      animation: gentleAppear 0.5s cubic-bezier(0.4, 0, 0.2, 1) forwards;
    }
    
    @keyframes gentleAppear {
      from {
        opacity: 0.9;
        transform: translateY(1px);
      }
      to {
        opacity: 1;
        transform: translateY(0);
      }
    }
  `;

  if (!user) {
    return null; // or a loading spinner
  }

  return (
    <div className="flex h-screen bg-gray-50">
      <style>{animationStyles}</style>
      
      <DashboardSidebar
        isOpen={isSidebarOpen}
        toggleSidebar={() => setIsSidebarOpen(!isSidebarOpen)}
        navItems={navItems}
        currentPath={location.pathname}
        navigate={navigate}
        user={user}
        onLogout={logout}
      />

      <div className="flex-1 overflow-auto">
        <DashboardHeader isAuthenticated={!!user} user={user} onLogout={logout} />
        
        <div className="p-6 space-y-6">
          {/* Success Message */}
          {saveSuccess && (
            <div className="fixed top-20 right-6 z-50 animate-fade-in">
              <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded-lg shadow-lg">
                <div className="flex items-center gap-2">
                  <CheckCircle className="h-5 w-5" />
                  <span>{t('notifications.settingsSaved', 'Notification settings saved successfully!')}</span>
                </div>
              </div>
            </div>
          )}

          {/* Error Message */}
          {error && (
            <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded-lg">
              <div className="flex items-center gap-2">
                <AlertCircle className="h-5 w-5" />
                <span>{error}</span>
              </div>
            </div>
          )}

          {/* Marking all as read indicator */}
          {markingAllAsRead && (
            <div className="fixed top-20 right-6 z-50 animate-fade-in">
              <div className="bg-blue-100 border border-blue-400 text-blue-700 px-4 py-3 rounded-lg shadow-lg">
                <div className="flex items-center gap-2">
                  <Loader2 className="h-5 w-5 animate-spin" />
                  <span>{t('notifications.markingAllRead', 'Marking all notifications as read...')}</span>
                </div>
              </div>
            </div>
          )}

          {/* Header with conditional premium gradient */}
          <div className={`${isPremium 
            ? 'bg-gradient-to-r from-[#253985] to-amber-500' 
            : 'bg-[#253985]'} text-white p-6 rounded-lg transition-all duration-500`}>
            <h1 className="text-2xl font-bold mb-2">{t('notifications.title', 'Notifications')}</h1>
            <p className="text-white/90">{t('notifications.subtitle', 'Manage your alerts and preferences')}</p>
          </div>

          <div className="bg-white rounded-2xl shadow-sm p-6 border border-gray-100">
            <div className="flex border-b border-gray-200 mb-6">
              <button
                onClick={() => setActiveTab('all')}
                className={`py-2 px-4 font-medium text-sm ${activeTab === 'all' ? 'text-[#253985] border-b-2 border-[#253985]' : 'text-gray-500 hover:text-gray-700'}`}
              >
                {t('notifications.allNotifications', 'All Notifications')}
              </button>
              <button
                onClick={() => setActiveTab('settings')}
                className={`py-2 px-4 font-medium text-sm ${activeTab === 'settings' ? 'text-[#253985] border-b-2 border-[#253985]' : 'text-gray-500 hover:text-gray-700'}`}
              >
                {t('notifications.notificationSettings', 'Notification Settings')}
              </button>
            </div>

            {activeTab === 'settings' ? (
              <div>
                <div className="flex justify-between items-center mb-6">
                  <h2 className="text-lg font-semibold text-[#253985]">
                    {t('notifications.preferences', 'Notification Preferences')}
                  </h2>
                  <div className="flex gap-2">
                    <button
                      onClick={fetchNotificationSettings}
                      disabled={loadingSettings}
                      className="flex items-center gap-1 px-3 py-1 text-sm text-gray-600 bg-gray-100 rounded-md hover:bg-gray-200"
                    >
                      <RefreshCw className={`h-4 w-4 ${loadingSettings ? 'animate-spin' : ''}`} />
                      {t('common.refresh', 'Refresh')}
                    </button>
                    <button
                      onClick={saveNotificationSettings}
                      disabled={savingSettings || loadingSettings}
                      className="flex items-center gap-1 px-3 py-1 text-sm text-white bg-[#253985] rounded-md hover:bg-[#1a2a6d]"
                    >
                      {savingSettings ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <Save className="h-4 w-4" />
                      )}
                      {t('common.saveChanges', 'Save Changes')}
                    </button>
                  </div>
                </div>
                <p className="text-gray-600 mb-6">
                  {t('notifications.preferencesDescription', 'Choose how you want to receive different types of notifications')}
                </p>

                {loadingSettings ? (
                  <div className="text-center py-12">
                    <Loader2 className="h-8 w-8 animate-spin mx-auto text-[#253985]" />
                    <p className="mt-3 text-gray-500">{t('notifications.loadingSettings', 'Loading settings...')}</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    <NotificationSettingToggle 
                      category="ShareSubs" 
                      label={t('notifications.shareSubsLabel', 'ShareSubs Notifications')}
                      description={t('notifications.shareSubsDescription', 'Get notified when someone shares a subscription with you')} 
                    />
                    <NotificationSettingToggle 
                      category="Security" 
                      label={t('notifications.securityLabel', 'Security Alerts')}
                      description={t('notifications.securityDescription', 'Important alerts about your account security')} 
                    />
                    <NotificationSettingToggle 
                      category="Promotion" 
                      label={t('notifications.promotionLabel', 'Promotional Offers')}
                      description={t('notifications.promotionDescription', 'Special deals and promotions from merchants')} 
                    />
                    <NotificationSettingToggle 
                      category="FreeTrialAlert" 
                      label={t('notifications.freeTrialLabel', 'Free Trial Alerts')}
                      description={t('notifications.freeTrialDescription', 'Reminders before your free trials end')} 
                    />
                    <NotificationSettingToggle 
                      category="PriceRadarAlert" 
                      label={t('notifications.priceRadarLabel', 'Price Radar Alerts')}
                      description={t('notifications.priceRadarDescription', 'Notifications when subscription prices change')} 
                    />
                  </div>
                )}
              </div>
            ) : (
              <>
                <div className="flex justify-between items-center mb-6">
                  <h2 className="text-lg font-semibold text-[#253985]">
                    {t('notifications.yourNotifications', 'Your Notifications')}
                  </h2>
                  <div className="flex gap-2">
                    {unreadCount > 0 && !markingAllAsRead && (
                      <button
                        onClick={smoothAnimateMarkAllAsRead}
                        disabled={markingAllAsRead}
                        className="flex items-center gap-1 px-3 py-1 text-sm text-white bg-green-600 rounded-md hover:bg-green-700 transition-colors"
                      >
                        <CheckCircle className="h-4 w-4" />
                        {t('notifications.markAllRead', 'Mark All as Read')}
                      </button>
                    )}
                    <button
                      onClick={fetchNotifications}
                      disabled={loading}
                      className="flex items-center gap-1 px-3 py-1 text-sm text-gray-600 bg-gray-100 rounded-md hover:bg-gray-200"
                    >
                      <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
                      {t('common.refresh', 'Refresh')}
                    </button>
                  </div>
                </div>

                {loading ? (
                  <div className="text-center py-12">
                    <Loader2 className="h-8 w-8 animate-spin mx-auto text-[#253985]" />
                    <p className="mt-3 text-gray-500">{t('notifications.loadingNotifications', 'Loading notifications...')}</p>
                  </div>
                ) : filteredNotifications.length > 0 ? (
                  <div className="space-y-4">
                    {filteredNotifications.map((notification, index) => {
                      const isAnimating = animatingNotifications.has(notification.id);
                      const isUnread = !notification.read && !isAnimating;
                      const isRead = notification.read || isAnimating;
                      const animationDelay = markingAllAsRead ? `${index * 40}ms` : '0ms';
                      
                      return (
                        <div 
                          key={notification.id}
                          className={`
                            notification-container
                            p-4 border rounded-lg flex flex-col cursor-pointer
                            ${isAnimating ? 'smooth-fade-out-blue' : ''}
                            ${isUnread ? 'border-[#253985] bg-blue-50' : 'border-gray-200'}
                            ${isRead && !isAnimating && animationStage === 'done' ? 'staggered-animation' : ''}
                          `}
                          style={{ 
                            animationDelay: isAnimating ? animationDelay : '0ms'
                          }}
                          onClick={() => handleNotificationClick(notification.id)}
                        >
                          <div className="flex items-start">
                            <div className="flex-shrink-0 mt-0.5 mr-4">
                              <notification.icon className={`h-5 w-5 ${notification.type === 'security' ? 'text-red-500' : 'text-[#253985]'}`} />
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex justify-between items-start">
                                <h3 className="font-medium text-gray-900">{notification.title}</h3>
                                <span className="text-xs text-gray-500 whitespace-nowrap ml-2">
                                  {notification.date}
                                </span>
                              </div>
                              <p className="text-sm text-gray-600 mt-1">
                                {expandedId === notification.id 
                                  ? notification.message 
                                  : `${notification.message.substring(0, 100)}${notification.message.length > 100 ? '...' : ''}`
                                }
                              </p>
                            </div>
                          </div>
                          
                          {expandedId === notification.id && (
                            <div className="mt-4 pt-4 border-t border-gray-200">
                              <div className="flex justify-between text-sm text-gray-500">
                                <span>{t('notifications.type', 'Type')}: <span className="capitalize">{notification.type}</span></span>
                                <span>{t('notifications.received', 'Received')}: {notification.fullDate}</span>
                              </div>
                            </div>
                          )}
                          
                          {isUnread && expandedId !== notification.id && (
                            <div 
                              className={`mt-2 text-right ${isAnimating ? 'smooth-fade-out-badge' : ''}`}
                              style={{ animationDelay: isAnimating ? animationDelay : '0ms' }}
                            >
                              <span className="inline-block px-2 py-1 text-xs bg-[#253985] text-white rounded-full transition-all duration-300">
                                {t('notifications.new', 'New')}
                              </span>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="text-center py-12">
                    <Calendar className="h-10 w-10 mx-auto text-gray-400 mb-3" />
                    <h3 className="text-lg font-medium text-gray-500">{t('notifications.noNotifications', 'No notifications')}</h3>
                    <p className="text-gray-400">{t('notifications.allCaughtUp', 'All caught up!')}</p>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Notifications;